import "./module.css";

import { CloudUploadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useUploadEventGalllary } from "features/events/use-cases/upload-event-gallary";
import { useEffect, useRef, useState } from "react";

export const EventGallaryModal = ({ id, onClose, gallaryModal, refetch, activeTab }: { id: string; onClose: () => void; gallaryModal: boolean; refetch: () => void; activeTab: string }) => {
    const { uploadImage: uploadGallaryImage, loading } = useUploadEventGalllary();
    
    const [uploadFiles, setUploadFiles] = useState<{ file: File, dataURL: string }[]>([]);
    const [error, setError] = useState<string[]>([]);

    const handleClose = () => {
        uploadFiles.forEach(item => {
            if (item.file.type.startsWith('video/')) {
                URL.revokeObjectURL(item.dataURL);
            }
        });
        onClose();
        setUploadFiles([]);
        setError([]);
    };

    const handleSuccess = () => {
        handleClose();
        refetch();
    };

    const fileInputRef = useRef<any>();

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const dataURLtoFile = (dataURL: string, fileName: string) => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)![1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        
return new File([u8arr], fileName, { type: mime });
    };

   const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    const defaultWidth = activeTab === "2" ? 1900 : 900;
     const defaultHeight = activeTab === "2" ? 800 : 515;


    if (!selectedFiles || selectedFiles.length === 0) return;

    const filesArray = Array.from(selectedFiles);

    const processedFiles: { file: File; dataURL: string }[] = [];
    const errors: string[] = [];

    const processImage = (file: File): Promise<void> => {
        return new Promise((resolve) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                const img = new Image();
                img.src = event?.target?.result as string;

                img.onload = () => {
                    const { width, height } = img;

                    if (width < defaultWidth || height < defaultHeight) {
                        errors.push(
                            `${file.name} - size is too small. Min: ${defaultWidth}x${defaultHeight}`
                        );
                        
return resolve();
                    }

                    // ✅ Resize using canvas
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");

                    const percent = (defaultHeight / defaultWidth) * 100;
                    const newHeight = (width * percent) / 100;

                    canvas.width = width;
                    canvas.height = newHeight;

                    ctx?.drawImage(img, 0, 0, width, newHeight);

                    // ✅ Force JPEG (fix for WebP issues)
                    const outputType = "image/jpeg";
                    const resizedImgDataUrl = canvas.toDataURL(outputType, 0.9);

                    const resizedFile = dataURLtoFile(
                        resizedImgDataUrl,
                        file.name.replace(/\.\w+$/, ".jpg")
                    );

                    processedFiles.push({
                        file: resizedFile,
                        dataURL: resizedImgDataUrl,
                    });

                    resolve();
                };

                img.onerror = () => {
                    errors.push(`${file.name} - failed to load image.`);
                    resolve();
                };
            };

            reader.onerror = () => {
                errors.push(`${file.name} - failed to read file.`);
                resolve();
            };

            reader.readAsDataURL(file);
        });
    };
    const processVideo = (file: File) => {
        const videoPreviewUrl = URL.createObjectURL(file);
        processedFiles.push({ file, dataURL: videoPreviewUrl });
    };
    // ✅ Process all files
    await Promise.all(
        filesArray.map(async (file) => {
            if (file.type.startsWith("video/")) {
                processVideo(file);
            } else if (file.type.startsWith("image/")) {
                await processImage(file);
            } else {
                errors.push(`${file.name} - format not supported.`);
            }
        })
    );

    // ✅ Final state update
    setUploadFiles(processedFiles);
    setError(errors);
};

    const handleUploadMedia = async () => {
        const typeId = activeTab === "1" ? "2" : "1";
        const formData = new FormData();
        
        uploadFiles.forEach((item) => {
            // Appends the actual File object (whether resized image or original video)
            formData.append(`files`, item.file, item.file.name);
        });

        uploadGallaryImage(id, typeId, formData, handleSuccess);
    };

    useEffect(() => {
        setUploadFiles([]);
        setError([]);
    }, [gallaryModal]);

    return (
        <>
            {gallaryModal ? (
                <div className='popup'>
                    <div className='modal_head'>
                        <h4>{activeTab === "2" ? "Upload Event Details Media" : "Upload Event Media"}</h4>
                        <button onClick={handleClose} className='close-btn'>&times;</button>
                    </div>
                    <div className='modal_body'>
                        {error.length > 0 && error.map((item, i) => (
                            <p key={i} style={{ color: "red", fontSize: '12px', margin: '4px 0' }}>{item}</p>
                        ))}

                        <div className="frmscrll">
                            <div className="imgup_wrap">
                                {uploadFiles.map((item, i) => (
                                    <div className="img_bx" key={i} style={{ overflow: 'hidden', borderRadius: '8px' }}>
                                        {item.file.type.startsWith('video/') ? (
                                            <video 
                                                src={item.dataURL} 
                                                autoPlay 
                                                muted 
                                                loop 
                                                playsInline
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                            />
                                        ) : (
                                            <img src={item.dataURL} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="upld_file_bx">
                                <CloudUploadOutlined />
                                <div className="upld_file">
                                    <label onClick={handleClick}>Choose Files </label>
                                    <span> {uploadFiles.length > 0 ? `${uploadFiles.length} files selected` : "No File Chosen"}</span>
                                    <input
                                        id="fileInput"
                                        ref={fileInputRef}
                                        type="file"
                                        style={{ display: 'none' }}
                                        accept="image/*,video/*"
                                        multiple
                                        onChange={handleFileChange}
                                    />
                                </div>
                                <p style={{ fontSize: '11px', color: '#666' }}>
                                    Supported: JPEG, PNG, WebP, MP4. 
                                  Min Image Size: {activeTab === "2"? "1900x800" : "900x515"}

                                    
                                </p>
                            </div>

                            <div className="upld_btn">
                                <Button 
                                    loading={loading} 
                                    className="save_btn" 
                                    disabled={uploadFiles.length < 1} 
                                    onClick={handleUploadMedia} 
                                    type="primary"
                                > 
                                    Save & Upload Media
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};